import { motion } from "framer-motion";
import { useContext } from "react";
import { ThemeContext } from "../../hooks/ThemeContext";
import { Palette, ShoppingBag, Users, Shield, TrendingUp, ShieldCheck } from "lucide-react";

const FeaturesSection = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const features = [
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Créations Uniques",
      desc: "Des designs originaux créés par des artistes talentueux",
      items: ["Bannieres premium", "Logos professionnels", "Illustrations sur mesure", "Templates reseaux sociaux"],
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Communauté Créative",
      desc: "Rejoignez une marketplace de créateurs passionnés",
      items: ["Createurs verifies", "Echange avec les artistes", "Portfolio professionnel", "Support communautaire"],
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Transactions Sécurisées",
      desc: "Achetez et vendez en toute confiance",
      items: ["Paiements securises", "Protection anti-copie", "Fichiers securises", "Garantie qualite"],
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Pour les Créateurs",
      desc: "Développez votre business créatif",
      items: ["Commissions equitables", "Gestion simplifiee", "Analytics detailles", "Paiements rapides"],
    },
    {
      icon: <ShoppingBag className="w-8 h-8" />,
      title: "Pour les Acheteurs",
      desc: "Trouvez exactement ce dont vous avez besoin",
      items: ["Catalogue varie", "Telechargement immediat", "Formats multiples", "Support 24h"],
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "Qualité Premium",
      desc: "Des créations vérifiées et approuvées",
      items: ["Controle qualite", "HD & Vector", "Fichiers sources", "Revisions incluses"],
    },
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto">
        <div
          className={`relative overflow-hidden border p-8 md:p-10 mb-12 shadow-xl ${
            isDark
              ? "border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20"
              : "border-rose-100/80 bg-gradient-to-br from-rose-50 via-white to-orange-50 shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
          }`}
        >
          <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${isDark ? "bg-rose-500/20" : "bg-rose-200/90"}`} />
          <div className={`absolute -left-12 -bottom-12 h-40 w-40 rounded-full ${isDark ? "bg-purple-500/15" : "bg-purple-100/80"}`} />

          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] ${
                  isDark ? "bg-white/10 text-white/80 border border-white/10" : "bg-white text-rose-600 border border-rose-100"
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                Expérience premium
              </div>
              <h2 className={`mt-4 text-3xl sm:text-4xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Pourquoi Krayne Agency
              </h2>
              <p className={`mt-3 text-lg ${isDark ? "text-white/70" : "text-gray-600"}`}>
                Une marketplace claire, rapide et securisee pour les creations digitales.
              </p>
            </div>
          </div>
        </div>

        <div
          className={`relative overflow-hidden border p-6 md:p-8 shadow-xl ${
            isDark
              ? "border-white/10 bg-white/5"
              : "border-rose-100/80 bg-gradient-to-br from-rose-50 via-white to-orange-50 shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
          }`}
        >
          <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${isDark ? "bg-rose-500/18" : "bg-rose-200/90"}`} />
          <div className={`absolute -left-12 -bottom-12 h-40 w-40 rounded-full ${isDark ? "bg-purple-500/10" : "bg-purple-100/80"}`} />

          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6  ">
            {features.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className={`group relative overflow-hidden p-6 border shadow-xl transition-all duration-300 bg-white/5 ${
                  isDark
                    ? "bg-white/[0.05] border-white/10 hover:border-rose-400/30 hover:bg-white/[0.06]"
                    : "bg-white/90 border-rose-100/80 hover:border-rose-200 hover:shadow-[0_18px_40px_rgba(15,23,42,0.06)]"
                }`}
              >
                {isDark && (
                  <>
                    <div className="pointer-events-none absolute -top-10 right-0 h-24 w-24 rounded-full bg-rose-500/10 blur-2xl" />
                    <div className="pointer-events-none absolute -bottom-10 left-0 h-24 w-24 rounded-full bg-purple-500/8 blur-2xl" />
                  </>
                )}

                <div
                  className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                    isDark ? "bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 text-rose-100 border border-white/10 shadow-xl" : "bg-rose-50 text-rose-600"
                  }`}
                >
                  {feature.icon}
                </div>

                <h3 className={`relative text-xl mb-2 font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {feature.title}
                </h3>

                <p className={`relative mb-4 ${isDark ? "text-white/65" : "text-gray-600"}`}>
                  {feature.desc}
                </p>

                <ul className={`relative space-y-2 text-sm ${isDark ? "text-white/55" : "text-gray-500"}`}>
                  {feature.items.map((item) => (
                    <li key={`${feature.title}-${item}`} className="flex items-center gap-2">
                      <span className={`${isDark ? "text-rose-400" : "text-rose-500"}`}>●</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
